/**
 * ContractWizard — F-003.2 Phase 3 (IMPL-P3-004) + F-003.4 Phase 4 (IMPL-P4-007)
 *                  + F-005 Phase 5 (IMPL-P5-003).
 *
 * Orchestratore del wizard 6-step (Locatore → Conduttore → Immobile →
 * Termini economici → IBAN AdE + Riepilogo → Invia).
 *
 * Responsabilità:
 *  - Instanzia useContractForm(opts) e wrappa tutto in FormProvider
 *  - Gestisce currentStep (0..5) e stepError state
 *  - Focus management: useEffect su currentStep → headingRef.current?.focus()
 *  - "Avanti" chiama validateStep(currentStep) — blocca se false e mostra Alert
 *  - "Indietro" non valida, non cancella dati
 *  - Step 4: IBAN AdE via Controller + riepilogo read-only
 *  - Step 5 (F-003.4): SubmitStep — PDF upload + consenso + invio
 *  - F-005: forwarding di `mode`, `initialData`, `errorFields` a `useContractForm`,
 *    e `initialStep` per aprire il wizard direttamente sul primo step contestato.
 *
 * Props:
 *  - praticaId: string — id della BOZZA creata da NuovaPraticaPage (create) o
 *                       id della pratica BLOCCATA (correct)
 *  - onSubmitted?: callback dopo submit; default no-op
 *  - mode?, initialData?, errorFields?, initialStep? — F-005 correctMode hooks
 */
import { useEffect, useRef, useState } from 'react';
import { Controller, FormProvider } from 'react-hook-form';
import type { UseFormReturn } from 'react-hook-form';
import { useContractForm } from '../../hooks/useContractForm';
import type { ContractFormMode } from '../../hooks/useContractForm';
import { SaveDraftIndicator } from './SaveDraftIndicator';
import { PartyCard } from './PartyCard';
import { SubmitStep } from './SubmitStep';
import { ImmobileFields } from '../fields/ImmobileFields';
import { CanoneFields } from '../fields/CanoneFields';
import { DurataFields } from '../fields/DurataFields';
import { Stepper } from './Stepper';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { IBANInput } from '@/features/profilo/components/IBANInput';
import type { PraticaView } from '../../lib/pratica-client';
import type { ContractDraft } from '@app/shared';

const STEP_LABELS = [
  'Locatore',
  'Conduttore',
  'Immobile',
  'Termini economici',
  'IBAN AdE',
  'Invia',
] as const;

export interface ContractWizardProps {
  praticaId: string;
  /** F-003.4: callback chiamata dopo submit riuscita (es. navigate alla detail). */
  onSubmitted?: (pratica: PraticaView) => void;
  /** F-005: 'correct' attiva la modalità correzione (autosave + submit endpoint diversi). */
  mode?: ContractFormMode;
  /** F-005: dati iniziali idratati one-shot nel form (correctMode). */
  initialData?: Partial<ContractDraft>;
  /** F-005: lista field-path contestati (passati down via context da NuovaPraticaPage). */
  errorFields?: string[];
  /** F-005: step di partenza (es. primo step con un campo contestato). */
  initialStep?: number;
  /** F-010: true per i privati → submit con pagamento Stripe ("Paga e invia €X"). */
  paymentMode?: boolean;
}

export function ContractWizard({
  praticaId,
  onSubmitted,
  mode = 'create',
  initialData,
  errorFields,
  initialStep = 0,
  paymentMode = false,
}: ContractWizardProps) {
  const { form, draftStatus, validateStep } = useContractForm({
    praticaId,
    mode,
    initialData,
    errorFields,
  });
  const clampedInitialStep = Math.min(
    Math.max(initialStep, 0),
    STEP_LABELS.length - 1,
  );
  const [currentStep, setCurrentStep] = useState(clampedInitialStep);
  const [stepError, setStepError] = useState<string | null>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);

  // Focus management: sposta il focus sul titolo del passo al cambio step
  // così lo screen reader annuncia la nuova sezione.
  useEffect(() => {
    headingRef.current?.focus();
  }, [currentStep]);

  const handleNext = async () => {
    setStepError(null);
    const ok = await validateStep(currentStep);
    if (!ok) {
      setStepError('Completa i campi richiesti prima di proseguire.');
      return;
    }
    setCurrentStep((s) => Math.min(s + 1, STEP_LABELS.length - 1));
  };

  const handleBack = () => {
    setStepError(null);
    setCurrentStep((s) => Math.max(s - 1, 0));
  };

  return (
    <FormProvider {...form}>
      <div className="space-y-6">
        {/* Header wizard: Stepper + SaveDraftIndicator */}
        <div className="flex items-start justify-between gap-4">
          <Stepper steps={STEP_LABELS} currentStep={currentStep} />
          <div className="flex-shrink-0 pt-1">
            <SaveDraftIndicator status={draftStatus} />
          </div>
        </div>

        {/* Alert errore validazione step (assertive: interrompe screen reader) */}
        {stepError && (
          <Alert variant="destructive" role="alert">
            {stepError}
          </Alert>
        )}

        {/* Titolo step — tabIndex={-1} per focus programmabile via JS,
            non raggiungibile con Tab keyboard (non è un controllo). */}
        <h2
          ref={headingRef}
          tabIndex={-1}
          className="text-2xl font-semibold tracking-tight focus:outline-none"
        >
          {STEP_LABELS[currentStep]}
        </h2>

        {/* Contenuto dello step corrente */}
        {currentStep === 0 && (
          <PartyCard prefix="locatore" title="Dati del locatore" showTooltips />
        )}
        {currentStep === 1 && (
          <PartyCard prefix="conduttore" title="Dati del conduttore" showTooltips />
        )}
        {currentStep === 2 && <ImmobileFields showTooltips />}
        {currentStep === 3 && (
          <div className="space-y-6">
            <CanoneFields />
            <DurataFields />
          </div>
        )}
        {currentStep === 4 && <WizardStep5Riepilogo form={form} />}
        {currentStep === 5 && (
          <SubmitStep
            praticaId={praticaId}
            mode={mode}
            paymentMode={paymentMode}
            onSubmitted={(pratica) => onSubmitted?.(pratica)}
          />
        )}

        {/* Navigation buttons */}
        <div className="flex items-center justify-between pt-4 border-t">
          <Button
            type="button"
            variant="ghost"
            onClick={handleBack}
            disabled={currentStep === 0}
          >
            Indietro
          </Button>

          {currentStep < STEP_LABELS.length - 1 && (
            <Button type="button" onClick={handleNext}>
              Avanti
            </Button>
          )}
          {/* All'ultimo step (Invia) il pulsante "Invia pratica" è dentro SubmitStep:
              non rendiamo qui un secondo CTA per evitare duplicazione. */}
        </div>
      </div>
    </FormProvider>
  );
}

/**
 * Step 5 interno: IBAN AdE + riepilogo read-only dei dati raccolti.
 * Il pulsante "Procedi" rimane disabled in F-003.2 — F-003.4 lo abiliterà.
 */
function WizardStep5Riepilogo({
  form,
}: {
  // Il wizard F-003 opera sempre su `ContractDraft`; annotazione esplicita
  // (invece di `ReturnType<typeof useContractForm>['form']`) perché ora l'hook
  // è generico e `ReturnType` sull'overload risolverebbe un `T` non vincolato.
  form: UseFormReturn<ContractDraft>;
}) {
  const values = form.getValues();
  const {
    control,
    formState: { errors },
  } = form;

  return (
    <div className="space-y-6">
      {/* IBAN AdE */}
      <div className="space-y-1">
        <label htmlFor="iban-ade" className="text-sm font-medium">
          IBAN per addebito AdE *
        </label>
        <Controller
          name="ibanAdE"
          control={control}
          render={({ field, fieldState }) => (
            <IBANInput
              id="iban-ade"
              value={field.value ?? ''}
              onChange={field.onChange}
              onBlur={field.onBlur}
              error={fieldState.error?.message}
            />
          )}
        />
        {errors.ibanAdE && (
          <p className="text-xs text-destructive">{errors.ibanAdE.message}</p>
        )}
      </div>

      {/* Riepilogo read-only */}
      <div className="rounded-lg border p-4 space-y-3">
        <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
          Riepilogo dati
        </h3>
        <dl className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
          <div>
            <dt className="text-muted-foreground">Locatore CF</dt>
            <dd className="font-mono">{values.locatore?.codiceFiscale ?? '—'}</dd>
          </div>
          <div>
            <dt className="text-muted-foreground">Conduttore CF</dt>
            <dd className="font-mono">{values.conduttore?.codiceFiscale ?? '—'}</dd>
          </div>
          <div>
            <dt className="text-muted-foreground">Tipologia</dt>
            <dd>{values.tipologia ?? '—'}</dd>
          </div>
          <div>
            <dt className="text-muted-foreground">Durata</dt>
            <dd>
              {values.durataMesi != null ? `${values.durataMesi} mesi` : '—'}
            </dd>
          </div>
          <div>
            <dt className="text-muted-foreground">Canone mensile</dt>
            <dd>
              {values.canoneMensile != null ? `€ ${values.canoneMensile}` : '—'}
            </dd>
          </div>
          <div>
            <dt className="text-muted-foreground">Regime fiscale</dt>
            <dd>{values.regimeFiscale ?? '—'}</dd>
          </div>
        </dl>
      </div>
    </div>
  );
}
